import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  User, 
  Mail, 
  Phone, 
  CheckCircle2,
  Loader2,
  Shield,
  Calendar,
  X,
  Star,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

// Country codes with flags
const countryCodes = [
  { code: '+91', country: 'IN', name: 'India', flag: '🇮🇳' },
  { code: '+1', country: 'US', name: 'USA', flag: '🇺🇸' },
  { code: '+44', country: 'GB', name: 'UK', flag: '🇬🇧' },
  { code: '+61', country: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: '+1', country: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: '+49', country: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'FR', name: 'France', flag: '🇫🇷' },
  { code: '+971', country: 'AE', name: 'UAE', flag: '🇦🇪' },
  { code: '+65', country: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: '+64', country: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
  { code: '+353', country: 'IE', name: 'Ireland', flag: '🇮🇪' },
  { code: '+31', country: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: '+41', country: 'CH', name: 'Switzerland', flag: '🇨🇭' },
  { code: '+92', country: 'PK', name: 'Pakistan', flag: '🇵🇰' },
  { code: '+880', country: 'BD', name: 'Bangladesh', flag: '🇧🇩' },
  { code: '+94', country: 'LK', name: 'Sri Lanka', flag: '🇱🇰' },
  { code: '+977', country: 'NP', name: 'Nepal', flag: '🇳🇵' },
];

export const EventRegistrationForm = ({ 
  event,
  isOpen,
  onClose,
  onSuccess
}) => {
  const { isDark } = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    countryCode: '+91',
    phone: '',
    agreeTerms: false,
    agreeContact: false
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        countryCode: '+91',
        phone: '',
        agreeTerms: false,
        agreeContact: false
      });
      setErrors({});
      setIsSuccess(false);
    }
  }, [isOpen]);

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email) return 'Email is required';
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return null;
  };

  // Phone validation
  const validatePhone = (phone) => {
    if (!phone) return 'Phone number is required';
    if (phone.length < 10) return 'Please enter a valid 10-digit phone number';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;
    const phoneError = validatePhone(formData.phone);
    if (phoneError) newErrors.phone = phoneError;
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    if (!formData.agreeTerms || !formData.agreeContact) {
      toast.error('Please accept the terms and consent to proceed');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const submitData = {
        event_id: event.id,
        event_title: event.title,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        country_code: formData.countryCode,
        phone: formData.phone,  // 10 digits only, country code sent separately
        trade_show_id: event.trade_show_id || null,
        trade_show_name: event.trade_show_name || null
      };
      
      const res = await fetch(`${API_URL}/api/public/events/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });
      
      if (res.ok) {
        setIsSuccess(true);
        toast.success('Registration successful! Check your email for confirmation.');
        onSuccess?.();
      } else {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Registration failed');
      }
    } catch (error) {
      toast.error(error.message || 'Something went wrong. Please try again.');
    }
    setIsSubmitting(false);
  };

  if (!event) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`sm:max-w-[500px] ${isDark ? 'bg-[#1e3340] border-white/10' : ''}`}>
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
            <Calendar className="w-5 h-5 text-ux-cyan" />
            Register for Event
          </DialogTitle>
        </DialogHeader>

        {/* Event Info */}
        <div className={`p-4 rounded-xl mb-4 ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
          <h3 className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-slate-800'}`}>
            {event.title}
          </h3>
          <p className={`text-sm mt-1 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
            {event.event_date && new Date(event.event_date).toLocaleDateString('en-IN', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
            {event.event_time && ` • ${event.event_time}`}
          </p>
          {event.location && (
            <p className={`text-sm mt-1 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
              📍 {event.location}
            </p>
          )}
        </div>

        {isSuccess ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-ux-green/20 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-ux-green" />
            </div>
            <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
              You're Registered!
            </h3>
            <p className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
              Check your email for confirmation details.
            </p>
            <Button onClick={onClose} className="mt-4 bg-ux-cyan hover:bg-ux-blue text-white">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                  First Name *
                </Label>
                <div className="relative">
                  <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
                  <Input
                    value={formData.firstName}
                    onChange={(e) => {
                      setFormData({...formData, firstName: e.target.value});
                      if (errors.firstName) setErrors({...errors, firstName: null});
                    }}
                    className={`pl-9 ${errors.firstName ? 'border-red-500' : ''} ${isDark ? 'bg-white/10 border-white/20 text-white' : ''}`}
                    placeholder="John"
                  />
                </div>
                {errors.firstName && (
                  <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>
                )}
              </div>
              <div>
                <Label className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                  Last Name *
                </Label>
                <Input
                  value={formData.lastName}
                  onChange={(e) => {
                    setFormData({...formData, lastName: e.target.value});
                    if (errors.lastName) setErrors({...errors, lastName: null});
                  }}
                  className={`${errors.lastName ? 'border-red-500' : ''} ${isDark ? 'bg-white/10 border-white/20 text-white' : ''}`}
                  placeholder="Doe"
                />
                {errors.lastName && (
                  <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <Label className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                Email Address *
              </Label>
              <div className="relative">
                <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({...formData, email: e.target.value});
                    if (errors.email) setErrors({...errors, email: null});
                  }}
                  className={`pl-9 ${errors.email ? 'border-red-500' : ''} ${isDark ? 'bg-white/10 border-white/20 text-white' : ''}`}
                  placeholder="john@email.com"
                />
              </div>
              {errors.email && (
                <p className="text-xs text-red-500 mt-1">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <Label className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                Mobile Number *
              </Label>
              <div className="flex gap-2">
                <Select 
                  value={formData.countryCode} 
                  onValueChange={(v) => setFormData({...formData, countryCode: v})}
                >
                  <SelectTrigger className={`w-[110px] ${isDark ? 'bg-white/10 border-white/20 text-white' : ''}`}>
                    <SelectValue>
                      {(() => {
                        const selected = countryCodes.find(c => c.code === formData.countryCode);
                        return selected ? `${selected.flag} ${selected.code}` : '+91';
                      })()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-[250px]">
                    {countryCodes.map((c, idx) => (
                      <SelectItem key={`${c.country}-${idx}`} value={c.code}>
                        <span className="flex items-center gap-2">
                          <span>{c.flag}</span>
                          <span>{c.code}</span>
                          <span className="text-xs text-slate-400">{c.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="relative flex-1">
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setFormData({...formData, phone: value});
                      if (errors.phone) setErrors({...errors, phone: null});
                    }}
                    className={`${errors.phone ? 'border-red-500' : ''} ${isDark ? 'bg-white/10 border-white/20 text-white' : ''}`}
                    placeholder="98765 43210"
                    maxLength={10}
                  />
                  {formData.phone.length === 10 && (
                    <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ux-green" />
                  )}
                </div>
              </div>
              {errors.phone && (
                <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
              )}
            </div>

            {/* Consent Checkboxes */}
            <div className={`space-y-3 p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.agreeTerms}
                  onChange={(e) => setFormData({...formData, agreeTerms: e.target.checked})}
                  className="mt-0.5 w-5 h-5 rounded border-2 border-slate-300 text-ux-cyan focus:ring-ux-cyan cursor-pointer"
                />
                <span className={`text-sm leading-tight ${isDark ? 'text-white/80' : 'text-slate-600'}`}>
                  I agree to the <Link to="/terms" className="text-ux-cyan hover:underline font-medium">Terms & Conditions</Link> and <Link to="/privacy" className="text-ux-cyan hover:underline font-medium">Privacy Policy</Link>
                </span>
              </label>
              
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.agreeContact}
                  onChange={(e) => setFormData({...formData, agreeContact: e.target.checked})}
                  className="mt-0.5 w-5 h-5 rounded border-2 border-slate-300 text-ux-cyan focus:ring-ux-cyan cursor-pointer"
                />
                <span className={`text-sm leading-tight ${isDark ? 'text-white/80' : 'text-slate-600'}`}>
                  I consent to being contacted via WhatsApp, Email & Phone regarding this event
                </span>
              </label>
            </div>

            {/* Trust Badges */}
            <div className="flex items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-1.5">
                <Shield className="w-4 h-4 text-ux-green" />
                <span className={isDark ? 'text-white/70' : 'text-slate-600'}>100% Secure</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Star className="w-4 h-4 text-ux-amber" />
                <span className={isDark ? 'text-white/70' : 'text-slate-600'}>4.9/5 Rating</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="w-4 h-4 text-ux-cyan" />
                <span className={isDark ? 'text-white/70' : 'text-slate-600'}>30K+ Students</span>
              </div>
            </div>

            {/* Submit Button */}
            <Button 
              type="submit" 
              disabled={isSubmitting || !formData.agreeTerms || !formData.agreeContact}
              className="w-full bg-gradient-to-r from-ux-cyan to-ux-blue hover:opacity-90 text-white py-3 font-semibold disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Registering...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Complete Registration
                </>
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EventRegistrationForm;
